/**
 * Insurance Payer Component
 *
 * Collects insurance payer information.
 * Reused for primary, secondary, and retry payer states.
 * LoadingBar and navigation buttons are rendered by the parent layout.
 */

"use client";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@frontend/shared/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "@frontend/lib/next-intl-typed";
import { useForm } from "react-hook-form";
import {
  useInsuranceConnectorContext,
  useInsuranceConnectorSend,
  useInsuranceConnectorState,
} from "../insurance-connector-provider";
import {
  type InsurancePayerFormData,
  insurancePayerSchema,
} from "../machine/schemas/validation.schemas";
import { INSURANCE_STEP_FORM_ID } from "../step-config";
import { PayerAutocomplete } from "./PayerAutocomplete";

export function InsurancePayer() {
  const context = useInsuranceConnectorContext();
  const send = useInsuranceConnectorSend();
  const state = useInsuranceConnectorState();
  const t = useTranslations("InsuranceConnector");

  const stateValue = state.value as { full?: string } | string;
  const fullState = typeof stateValue === "object" ? stateValue.full : "";
  const isSecondary = fullState?.includes("secondary") ?? false;
  const isRetry = fullState?.includes("unverifiedRetry") ?? false;
  const insurance = isSecondary
    ? context.secondaryInsurance
    : context.primaryInsurance;

  const form = useForm<InsurancePayerFormData>({
    resolver: zodResolver(insurancePayerSchema),
    defaultValues: {
      payer: insurance?.payer || "",
      payerId: insurance?.payerId || "",
    },
  });

  const onSubmit = (data: InsurancePayerFormData) => {
    if (isSecondary) {
      send({
        type: "SUBMIT",
        data: {
          secondaryPayer: data.payer,
          secondaryPayerId: data.payerId,
        },
      });
    } else {
      send({ type: "SUBMIT", data });
    }
  };

  const headerText = isRetry
    ? t("ConfirmPayerEdit")
    : isSecondary
      ? t("SecondaryPayerQuestion")
      : t("PrimaryPayerQuestion");

  return (
    <Form {...form}>
      <form
        id={INSURANCE_STEP_FORM_ID}
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-4 md:gap-8"
      >
        <h2 className="text-2xl md:text-[32px] font-semibold leading-[120%]">
          {headerText}
        </h2>
        <FormField
          control={form.control}
          name="payer"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel className="sr-only">{t("InsurancePayer")}</FormLabel>
              <FormControl>
                <PayerAutocomplete
                  value={field.value}
                  onChange={(value, payerId) => {
                    field.onChange(value);
                    form.setValue("payerId", payerId);
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
}
